import React, { useState, useEffect, useRef } from 'react';
// eslint-disable-next-line no-unused-vars
import { motion, AnimatePresence } from 'framer-motion';
import { X, User, BookOpen, GraduationCap, Plus, Check, Mail, Save, Loader2, FileText, Sun, Moon } from 'lucide-react';
import api from '../services/api';
import TeacherContextPanel from './TeacherContextPanel';
import ContextFileUploader from './ContextFileUploader';
import { useTheme } from '../contexts/ThemeContext';

const GRADE_OPTIONS = [
    'Pre-K', 'Kindergarten', '1st Grade', '2nd Grade', '3rd Grade', '4th Grade',
    '5th Grade', '6th Grade', '7th Grade', '8th Grade', '9th Grade',
    '10th Grade', '11th Grade', '12th Grade'
];

const SUBJECT_SUGGESTIONS = [
    // Mathematics
    'Algebra', 'Geometry', 'Calculus', 'Pre-Algebra', 'Trigonometry', 'Statistics',
    'Pre-Calculus', 'AP Calculus', 'Math', 'General Math',
    // Science
    'Physics', 'Chemistry', 'Biology', 'Earth Science', 'Environmental Science',
    'AP Physics', 'AP Chemistry', 'AP Biology', 'Science', 'Life Science',
    // English
    'English', 'English/ELA', 'Literature', 'Writing', 'Reading', 'Grammar',
    'AP English', 'Creative Writing', 'Journalism', 'ESL/ELL',
    // Social Studies/History
    'US History', 'World History', 'Geography', 'Government', 'Economics',
    'AP US History', 'AP World History', 'Civics', 'Social Studies',
    // Languages
    'Spanish', 'French', 'German', 'Mandarin', 'Latin', 'ASL',
    // Engineering & Tech
    'Engineering', 'Computer Science', 'Robotics', 'Web Development', 'Coding',
    // Arts
    'Art', 'Music', 'Band', 'Choir', 'Theater', 'Dance', 'Photography',
    // Other
    'Physical Education', 'Health', 'Special Education', 'SPED', 'Electives'
];

/* ── Appearance Tab (light/dark mode) ── */
const AppearanceTab = () => {
    const { isDark, setDark } = useTheme();

    return (
        <div className="space-y-6">
            <div>
                <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-3">
                    <Sun size={16} className="text-amber-500" />
                    Theme
                </label>
                <div className="grid grid-cols-2 gap-3">
                    <button
                        onClick={() => setDark(false)}
                        className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-all ${
                            !isDark
                                ? 'border-amber-400 bg-amber-50 shadow-sm'
                                : 'border-slate-200 bg-white hover:border-slate-300'
                        }`}
                    >
                        <Sun size={20} className={!isDark ? 'text-amber-500' : 'text-slate-400'} />
                        <div className="text-left">
                            <div className={`font-semibold text-sm ${!isDark ? 'text-amber-700' : 'text-slate-600'}`}>
                                Light Mode
                            </div>
                            <div className="text-xs text-slate-400">Clean and bright</div>
                        </div>
                    </button>
                    <button
                        onClick={() => setDark(true)}
                        className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-all ${
                            isDark
                                ? 'border-indigo-400 bg-indigo-50 shadow-sm'
                                : 'border-slate-200 bg-white hover:border-slate-300'
                        }`}
                    >
                        <Moon size={20} className={isDark ? 'text-indigo-500' : 'text-slate-400'} />
                        <div className="text-left">
                            <div className={`font-semibold text-sm ${isDark ? 'text-indigo-700' : 'text-slate-600'}`}>
                                Dark Mode
                            </div>
                            <div className="text-xs text-slate-400">Easy on the eyes</div>
                        </div>
                    </button>
                </div>
                <p className="text-xs text-slate-400 mt-3">
                    Your preference is saved automatically.
                </p>
            </div>
        </div>
    );
};

const AccountSettingsModal = ({ isOpen, onClose, userProfile, onUpdateProfile }) => {
    const [activeTab, setActiveTab] = useState('profile');
    const [saving, setSaving] = useState(false);
    const [saveSuccess, setSaveSuccess] = useState(false);
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

    // Toast helper function
    // eslint-disable-next-line no-unused-vars
    const showToast = (message, type = 'success') => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 2500);
    };

    // Profile state
    const [name, setName] = useState(userProfile?.name || '');
    const [gradeLevel, setGradeLevel] = useState(userProfile?.grade_level || '');
    // Ensure subjects is always an array
    const [subjects, setSubjects] = useState(() => {
        const subj = userProfile?.subjects;
        if (Array.isArray(subj)) return subj;
        if (typeof subj === 'string' && subj) return [subj];
        return [];
    });
    const [district, setDistrict] = useState(userProfile?.district || '');

    // Subject input state for freeform autocomplete
    const [subjectInput, setSubjectInput] = useState('');
    const [showSubjectSuggestions, setShowSubjectSuggestions] = useState(false);
    const subjectInputRef = useRef(null);




    // Fetch profile and district on mount
    useEffect(() => {

        const fetchSchoolProfile = async () => {
            try {
                const res = await api.get('/get-school-profile', { params: { email: userProfile.email } });
                if (res.data.profile) {
                    const profile = res.data.profile;
                    // Update all profile fields from school_profile
                    if (profile.district) {
                        setDistrict(profile.district);
                    }
                    if (profile.grade_level) {
                        setGradeLevel(profile.grade_level);
                    }
                    if (profile.subjects) {
                        // Ensure subjects is always an array
                        const subj = profile.subjects;
                        if (Array.isArray(subj)) {
                            setSubjects(subj);
                        } else if (typeof subj === 'string' && subj) {
                            setSubjects([subj]);
                        }
                    }
                }
            } catch (e) {
                console.error('Failed to fetch school profile:', e);
            }
        };

        if (isOpen && userProfile?.email) {
            fetchSchoolProfile();
        }
    }, [isOpen, userProfile?.email]);


    const handleSaveProfile = async () => {
        setSaving(true);
        try {
            await api.post('/api/update-user', {
                email: userProfile.email,
                grade_level: gradeLevel,
                subjects: subjects,
                district: district
            });

            setSaveSuccess(true);
            setTimeout(() => setSaveSuccess(false), 2000);

            // Update parent state if callback provided
            const updatedProfile = { ...userProfile, grade_level: gradeLevel, subjects: subjects, district: district };
            if (onUpdateProfile) {
                onUpdateProfile(updatedProfile);
            }
            // Persist to localStorage so changes survive page refresh
            localStorage.setItem('saige_profile', JSON.stringify(updatedProfile));
        } catch (e) {
            console.error('Failed to save profile:', e);
            alert('Failed to save profile. Please try again.');
        } finally {
            setSaving(false);
        }
    };



    // Add a new subject (either from suggestions or custom)
    const addSubject = (subject) => {
        const trimmed = subject.trim();
        const currentSubjects = Array.isArray(subjects) ? subjects : [];
        if (trimmed && !currentSubjects.includes(trimmed)) {
            setSubjects([...currentSubjects, trimmed]);
        }
        setSubjectInput('');
        setShowSubjectSuggestions(false);
    };

    // Remove a subject
    const removeSubject = (subject) => {
        setSubjects(prev => prev.filter(s => s !== subject));
    };

    // Get filtered suggestions based on input
    const getFilteredSuggestions = () => {
        if (!subjectInput.trim()) return [];
        const input = subjectInput.toLowerCase();
        const currentSubjects = Array.isArray(subjects) ? subjects : [];
        return SUBJECT_SUGGESTIONS.filter(
            s => s.toLowerCase().includes(input) && !currentSubjects.includes(s)
        ).slice(0, 8);
    };





    if (!isOpen) return null;

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40 backdrop-blur-sm"
                onClick={onClose}
            >
                <motion.div
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.9, opacity: 0 }}
                    className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 max-h-[85vh] overflow-hidden flex flex-col"
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Header */}
                    <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                        <h2 className="text-xl font-bold text-slate-800">Account Settings</h2>
                        <button
                            onClick={onClose}
                            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                        >
                            <X size={20} />
                        </button>
                    </div>

                    {/* Tabs */}
                    <div className="px-6 pt-4 flex gap-2 border-b border-slate-100">
                        {[
                            { id: 'profile', label: 'Profile', icon: User },
                            { id: 'context', label: 'Course Context', icon: FileText },
                            { id: 'appearance', label: 'Appearance', icon: Sun }
                        ].map(tab => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`flex items-center gap-2 px-4 py-2.5 rounded-t-lg font-medium text-sm transition-colors ${activeTab === tab.id
                                    ? 'bg-amber-50 text-amber-600 border-b-2 border-amber-500'
                                    : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                                    }`}
                            >
                                <tab.icon size={16} />
                                {tab.label}
                            </button>
                        ))}
                    </div>

                    {/* Content */}
                    <div className="flex-1 overflow-y-auto p-6">
                        {/* Profile Tab */}
                        {activeTab === 'profile' && (
                            <div className="space-y-6">
                                {/* Avatar & Name */}
                                <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl">
                                    <div className="w-16 h-16 bg-gradient-to-tr from-yellow-400 to-amber-500 rounded-xl flex items-center justify-center text-white font-bold text-2xl shadow-lg">
                                        {name?.[0]?.toUpperCase() || 'T'}
                                    </div>
                                    <div className="flex-1">
                                        <input
                                            type="text"
                                            value={name}
                                            onChange={(e) => setName(e.target.value)}
                                            className="w-full font-bold text-lg text-slate-800 bg-transparent border-b border-transparent hover:border-slate-200 focus:border-amber-400 outline-none py-1 transition-colors"
                                            placeholder="Your name"
                                        />
                                        <div className="flex items-center gap-2 text-sm text-slate-500 mt-1">
                                            <Mail size={14} />
                                            {userProfile.email}
                                        </div>
                                    </div>
                                </div>

                                {/* District (New) */}
                                <div>
                                    <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-2">
                                        <User size={16} className="text-amber-500" />
                                        School District
                                    </label>
                                    <input
                                        type="text"
                                        value={district}
                                        onChange={(e) => setDistrict(e.target.value)}
                                        className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none transition-all"
                                        placeholder="e.g. Austin ISD"
                                    />
                                </div>

                                {/* Grade Level */}
                                <div>
                                    <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-2">
                                        <GraduationCap size={16} className="text-amber-500" />
                                        Grade Level
                                    </label>
                                    <select
                                        value={gradeLevel}
                                        onChange={(e) => setGradeLevel(e.target.value)}
                                        className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none transition-all"
                                    >
                                        <option value="">Select grade level...</option>
                                        {GRADE_OPTIONS.map(grade => (
                                            <option key={grade} value={grade}>{grade}</option>
                                        ))}
                                    </select>
                                </div>

                                {/* Subjects - Freeform Multi-Select */}
                                <div>
                                    <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-2">
                                        <BookOpen size={16} className="text-amber-500" />
                                        Subjects You Teach
                                    </label>

                                    {/* Selected subjects as chips */}
                                    <div className="flex flex-wrap gap-2 mb-3">
                                        {(Array.isArray(subjects) ? subjects : []).map(subject => (
                                            <span
                                                key={subject}
                                                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-100 text-amber-800 rounded-full text-sm font-medium"
                                            >
                                                {subject}
                                                <button
                                                    onClick={() => removeSubject(subject)}
                                                    className="hover:bg-amber-200 rounded-full p-0.5 transition-colors"
                                                >
                                                    <X size={14} />
                                                </button>
                                            </span>
                                        ))}
                                    </div>

                                    {/* Subject input with autocomplete */}
                                    <div className="relative">
                                        <input
                                            ref={subjectInputRef}
                                            type="text"
                                            placeholder="Type to search or add a subject..."
                                            value={subjectInput}
                                            onChange={(e) => {
                                                setSubjectInput(e.target.value);
                                                setShowSubjectSuggestions(true);
                                            }}
                                            onFocus={() => setShowSubjectSuggestions(true)}
                                            onBlur={() => setTimeout(() => setShowSubjectSuggestions(false), 200)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' && subjectInput.trim()) {
                                                    e.preventDefault();
                                                    addSubject(subjectInput);
                                                }
                                            }}
                                            className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none transition-all"
                                        />
                                        <Plus size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />

                                        {/* Suggestions dropdown */}
                                        {showSubjectSuggestions && getFilteredSuggestions().length > 0 && (
                                            <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg max-h-48 overflow-y-auto">
                                                {getFilteredSuggestions().map(suggestion => (
                                                    <button
                                                        key={suggestion}
                                                        onClick={() => addSubject(suggestion)}
                                                        className="w-full px-4 py-2.5 text-left hover:bg-amber-50 text-slate-700 text-sm transition-colors first:rounded-t-xl last:rounded-b-xl"
                                                    >
                                                        {suggestion}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    <p className="text-xs text-slate-400 mt-2">Type any subject and press Enter to add it</p>
                                </div>

                                {/* Save Button */}
                                {(!gradeLevel || !(Array.isArray(subjects) ? subjects : []).length) && (
                                    <p className="text-sm text-amber-600 bg-amber-50 px-3 py-2 rounded-lg">
                                        Grade level and at least one subject are required.
                                    </p>
                                )}
                                <button
                                    onClick={handleSaveProfile}
                                    disabled={saving || !gradeLevel || !(Array.isArray(subjects) ? subjects : []).length}
                                    className="w-full py-3 bg-amber-500 text-white rounded-xl font-medium hover:bg-amber-600 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {saving ? (
                                        <Loader2 size={18} className="animate-spin" />
                                    ) : saveSuccess ? (
                                        <>
                                            <Check size={18} />
                                            Saved!
                                        </>
                                    ) : (
                                        <>
                                            <Save size={18} />
                                            Save Changes
                                        </>
                                    )}
                                </button>
                            </div>
                        )}


                        {/* Course Context Tab */}
                        {activeTab === 'context' && (
                            <div className="space-y-5">
                                {/* General Saige context documents — persisted via /upload-document */}
                                <div>
                                    <p className="text-[11px] text-slate-400 mb-2">
                                        Upload pacing guides, syllabi, or reference docs. These inform all Saige interactions.
                                    </p>
                                    <ContextFileUploader />
                                </div>

                                {/* Subject-specific course context — structured via /api/teacher-context */}
                                <div>
                                    <p className="text-[11px] text-slate-400 mb-2">
                                        Manage subject-specific scope &amp; sequence and course objectives.
                                    </p>
                                    <TeacherContextPanel
                                        selectedSubject={subjects[0] || ''}
                                        subjects={subjects}
                                    />
                                </div>
                            </div>
                        )}

                        {/* Appearance Tab */}
                        {activeTab === 'appearance' && (
                            <AppearanceTab />
                        )}
                    </div>
                </motion.div>
            </motion.div>

            {/* Toast Notification */}
            {toast.show && (
                <motion.div
                    initial={{ opacity: 0, y: 50 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 50 }}
                    className={`fixed bottom-6 right-6 px-4 py-3 rounded-xl shadow-lg flex items-center gap-2 z-[60] ${toast.type === 'error'
                        ? 'bg-red-500 text-white'
                        : 'bg-emerald-500 text-white'
                        }`}
                >
                    {toast.type === 'error' ? (
                        <X size={18} />
                    ) : (
                        <Check size={18} />
                    )}
                    <span className="font-medium">{toast.message}</span>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default AccountSettingsModal;
